/**
 * Beacon "abrió el checkout" → DOS sinks paralelos:
 *   1. Slack funnel (#juaperrone) vía /api/track-funnel-failure (kind=checkout_opened)
 *   2. PostHog event `checkout_opened` vía mirror en captureAttribution()
 *
 * Se dispara justo ANTES de redirigir al checkout hosteado (MercadoPago / Stripe),
 * con `keepalive: true` para que el POST sobreviva a `window.location.assign`.
 * Best-effort: nunca bloquea ni interrumpe el checkout.
 *
 * Embudo completo:
 *   cta_click_* (click) → checkout_opened (abrió) → purchase_completed_server
 *   (webhook, server-side). Permite ver cuántos clickean vs cuántos
 *   efectivamente llegan al checkout, por gateway y monto.
 */

import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
import { captureAttribution } from "@/lib/captureAttribution";

export type CheckoutGateway = "mercadopago" | "stripe" | "fan_fallback";

export interface CheckoutOpenedParams {
  gateway: CheckoutGateway;
  /** ISO2 del visitante (ya resuelto por el caller). */
  country: string;
  /** flow / template para agrupar en Slack (ej. "premium_website"). */
  template: string;
  /** Monto en centavos. ARS = pesos*100; USD = cents. */
  amountCents?: number;
  currency?: string;
  planTier?: string;
  productName?: string;
  flowTraceId?: string;
}

export function trackCheckoutOpenedSlack(params: CheckoutOpenedParams): void {
  // 1) attribution_events + mirror PostHog `checkout_opened` (growth funnel / reporte mensual del admin).
  // Mismo evento que PostHog/Slack pero persistido en Postgres con event_name
  // 'checkout_opened' — es lo que cuenta growth-monthly-report. best-effort.
  try {
    captureAttribution("checkout_opened", {
      country: params.country,
      template: params.template,
      valueCents: params.amountCents,
      currency: params.currency,
      metadata: {
        gateway: params.gateway,
        plan_tier: params.planTier,
        product_name: params.productName,
        flow_trace_id: params.flowTraceId,
      },
    });
  } catch {
    // best-effort — nunca interrumpe el checkout
  }

  // 2) Slack via funnel-failure endpoint (keepalive sobrevive al redirect)
  try {
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        kind: "checkout_opened",
        template: params.template || "checkout",
        country: params.country || "UNKNOWN",
        amount_cents: params.amountCents,
        currency: params.currency,
        flow_trace_id: params.flowTraceId,
        page_url: typeof window !== "undefined" ? window.location.href : undefined,
        extra: {
          ...getHeroCtaSlackExtra(),
          gateway: params.gateway,
          plan_tier: params.planTier,
          product_name: params.productName,
        },
      }),
    }).catch(() => {});
  } catch {
    // best-effort — nunca interrumpe el checkout
  }
}
